import React, { useCallback, useEffect, useRef, useState } from 'react';
import { isEmpty } from 'lodash';
import { KEYS } from './constants';

const KEYDOWN = 'keydown';

const useMenuNavigation = ({ ref }) => {
    const [hover, setHover] = useState();
    const menuItems = useRef([]).current;

    const addMenuItem = useCallback((menuItemRef, onClick = Function.prototype) => {
        const newMenuItems = [...menuItems, { ref: menuItemRef, onClick }]
            .sort((firstItem, secItem) => (
                firstItem.ref.current.offsetTop - secItem.ref.current.offsetTop
            ));

        menuItems.splice(0, menuItems.length);
        menuItems.push(...newMenuItems);
    }, [menuItems]);

    const removeMenuItem = useCallback((menuItemRef) => {

        const index = menuItems.findIndex(({ ref }) => menuItemRef === ref);
        if (index > -1) {
            menuItems.splice(index, 1);
        }
    }, [menuItems]);

    const getMenuItemIndex = useCallback((toFind) => menuItems.findIndex(({ ref }) => ref === toFind), [menuItems]);

    const moveUpHandler = useCallback((event) => {
        const index = getMenuItemIndex(hover);

        switch (true) {
            case index === 0:
                setHover(menuItems[menuItems.length - 1].ref);
                break;
            case index > 0:
                setHover(menuItems[index - 1].ref);
                break;
            case !isEmpty(menuItems):
                setHover(menuItems[0].ref);
                break;
            default:
                setHover(null);
                break;
        }
        event.preventDefault();
    }, [menuItems, setHover, hover, getMenuItemIndex]);

    const moveDownHandler = useCallback((event) => {
        const index = getMenuItemIndex(hover);

        switch (true) {
            case index === menuItems.length - 1:
                setHover(menuItems[0].ref);
                break;
            case index > -1:
                setHover(menuItems[index + 1].ref);
                break;
            case !isEmpty(menuItems):
                setHover(menuItems[0].ref);
                break;
            default:
                setHover(null);
                break;
        }
        event.preventDefault();
    }, [menuItems, setHover, hover, getMenuItemIndex]);

    const select = useCallback((event) => {
        const index = getMenuItemIndex(hover);

        if (index > -1) {
            menuItems[index].onClick();
        }
        event.preventDefault();
    }, [menuItems, hover, getMenuItemIndex]);

    useEffect(() => {
        const listenerTarget = ref.current;
        const keyToHandler = {
            [KEYS.ARROW_DOWN]: moveDownHandler,
            [KEYS.ARROW_UP]: moveUpHandler,
            [KEYS.ENTER]: select
        };

        const keyDownHandler = (keyDownEvent) => {
            const { key } = keyDownEvent;
            const handler = keyToHandler[key.toLowerCase()];

            handler && handler(keyDownEvent);
        };

        listenerTarget.addEventListener(KEYDOWN, keyDownHandler);
        return () => {
            listenerTarget.removeEventListener(KEYDOWN, keyDownHandler);
        };
    }, [ref, hover, moveUpHandler, moveDownHandler, select]);

    return {
        getMenuItemIndex,
        addMenuItem,
        removeMenuItem,
        hover,
        setHover
    };
};

export default useMenuNavigation;
